import { useEffect, useMemo, useRef, type ComponentProps } from 'react';
import ReactMarkdown from 'react-markdown';
import type { ChatMessageType } from '@/store/types';
import MarkdownWithMath from './MarkdownWithMath';
import WorkflowStepsCard from './WorkflowStepsCard';
import { useSmoothTypewriter } from './useSmoothTypewriter';
import {
  decodeSuggestedQuestionLink,
  isSuggestedQuestionLink,
  SUGGESTED_QUESTION_LINK_PREFIX,
  transformSuggestedQuestionTags,
} from './parseSuggestedQuestions';
import { parseApiEndpointLinks, stripApiEndpointTags, type ApiEndpointSearchLink } from './parseApiEndpoint';
import { parseWorkflowSteps } from './parseWorkflowSteps';

type BotMessageTextProps = {
  msg: ChatMessageType;
  isLiveStream: boolean;
  streamBufferClosed: boolean;
  suggestionsDisabled?: boolean;
  searchSource?: string;
  normalizeMarkdown: (text: string) => string;
  markdownComponents: ComponentProps<typeof ReactMarkdown>['components'];
  onRevealCaughtUp: () => void;
  onSyncInterruptedDisplay: (messageId: string, displayText: string) => void;
  onSuggestedQuestionClick?: (question: string) => void;
  onApiEndpointClick?: (link: ApiEndpointSearchLink) => void;
  onVisibleTick?: () => void;
};

export default function BotMessageText({
  msg,
  isLiveStream,
  streamBufferClosed,
  suggestionsDisabled = false,
  searchSource = 'ALL',
  normalizeMarkdown,
  markdownComponents,
  onRevealCaughtUp,
  onSyncInterruptedDisplay,
  onSuggestedQuestionClick,
  onApiEndpointClick,
  onVisibleTick,
}: BotMessageTextProps) {
  const target = isLiveStream ? (msg.streamTarget ?? '') : msg.message;
  const streamBufferClosedRef = useRef(streamBufferClosed);
  streamBufferClosedRef.current = streamBufferClosed;

  const { visible, visibleRef } = useSmoothTypewriter(target, {
    active: isLiveStream,
    streamId: msg.id,
    onCaughtUp: () => {
      if (streamBufferClosedRef.current) onRevealCaughtUp();
    },
  });

  const displayText = isLiveStream || (msg.interrupted && !msg.message.trim()) ? visible : msg.message;

  const fullMessageText = msg.streamTarget ?? msg.message;
  const { cleanedText, links: apiEndpointLinks } = useMemo(
    () => parseApiEndpointLinks(fullMessageText, { source: searchSource }),
    [fullMessageText, searchSource],
  );

  const showApiEndpointLinks = !isLiveStream && apiEndpointLinks.length > 0;

  const markdownSourceText = isLiveStream ? stripApiEndpointTags(displayText) : cleanedText;

  const markdownWithSuggestedQuestions = useMemo(
    () => normalizeMarkdown(transformSuggestedQuestionTags(markdownSourceText)),
    [markdownSourceText, normalizeMarkdown],
  );

  const hasSuggestedQuestions = markdownWithSuggestedQuestions.includes(SUGGESTED_QUESTION_LINK_PREFIX);

  const workflow = useMemo(
    () => (isLiveStream ? null : parseWorkflowSteps(markdownWithSuggestedQuestions)),
    [isLiveStream, markdownWithSuggestedQuestions],
  );

  const resolvedMarkdownComponents = useMemo<ComponentProps<typeof ReactMarkdown>['components']>(() => {
    const defaultAnchor = markdownComponents?.a;

    return {
      ...markdownComponents,
      a: ({ href, children, ...props }) => {
        if (isSuggestedQuestionLink(href)) {
          const question = decodeSuggestedQuestionLink(href);

          return (
            <button
              type="button"
              className="aida-suggested-questions__chip"
              disabled={suggestionsDisabled || !onSuggestedQuestionClick}
              data-demo-tour="aida-suggested-question-chip"
              onClick={event => {
                event.preventDefault();
                onSuggestedQuestionClick?.(question);
              }}
            >
              {children}
            </button>
          );
        }

        if (defaultAnchor) {
          const AnchorComponent = defaultAnchor;
          return (
            <AnchorComponent href={href} {...props}>
              {children}
            </AnchorComponent>
          );
        }

        return (
          <a href={href ?? undefined} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      },
    };
  }, [markdownComponents, onSuggestedQuestionClick, suggestionsDisabled]);

  const didMountFinalizeRef = useRef(false);
  useEffect(() => {
    if (didMountFinalizeRef.current) return;
    didMountFinalizeRef.current = true;
    if (isLiveStream && streamBufferClosed) {
      onRevealCaughtUp();
    }
  }, []);

  useEffect(() => {
    if (!isLiveStream || !streamBufferClosed) return;
    if (visible.length >= target.length) {
      onRevealCaughtUp();
    }
  }, [isLiveStream, streamBufferClosed, visible, target, onRevealCaughtUp]);

  const didSyncInterruptedRef = useRef(false);
  useEffect(() => {
    if (!msg.interrupted) {
      didSyncInterruptedRef.current = false;
      return;
    }
    if (isLiveStream || didSyncInterruptedRef.current) return;
    didSyncInterruptedRef.current = true;
    onSyncInterruptedDisplay(msg.id, visibleRef.current);
  }, [msg.id, msg.interrupted, isLiveStream, onSyncInterruptedDisplay, visibleRef]);

  useEffect(() => {
    if (isLiveStream && visible) onVisibleTick?.();
  }, [isLiveStream, visible, onVisibleTick]);

  if (!markdownWithSuggestedQuestions.trim() && !msg.interrupted && !showApiEndpointLinks) return null;

  return (
    <div
      className={`aida-msg__text${isLiveStream ? ' aida-msg__text--typing' : ''}${
        hasSuggestedQuestions ? ' aida-msg__text--with-inline-questions' : ''
      }`}
    >
      {workflow ? (
        <>
          {workflow.intro ? (
            <MarkdownWithMath content={workflow.intro} markdownComponents={resolvedMarkdownComponents} />
          ) : null}
          <WorkflowStepsCard
            key={msg.id}
            steps={workflow.steps}
            sourceText={markdownWithSuggestedQuestions}
            markdownComponents={resolvedMarkdownComponents}
          />
          {workflow.outro ? (
            <MarkdownWithMath content={workflow.outro} markdownComponents={resolvedMarkdownComponents} />
          ) : null}
        </>
      ) : (
        <MarkdownWithMath
          content={markdownWithSuggestedQuestions}
          markdownComponents={resolvedMarkdownComponents}
        />
      )}
      {showApiEndpointLinks && (
        <div className="aida-api-endpoint-links">
          {apiEndpointLinks.map((link, index) => (
            <a
              key={`${link.search}-${index}`}
              href={`${link.pathname}${link.search}`}
              target="_blank"
              rel="noopener noreferrer"
              className="aida-api-endpoint-links__button"
              onClick={() => onApiEndpointClick?.(link)}
            >
              {link.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
